import {
  DisplayType,
  FieldTypeEnum,
  FormattingType,
  FormattingValueType,
  KeyValueFormattingModel,
  KeyValueRestrictionModel,
  RestrictionType,
  RestrictionValueType,
} from '@models/module/fields/enums';

export interface FieldTypeIndicatorField {
  typeId?: number;
  displayTypeId?: number;
  hasMultipleValues?: boolean;
  restrictions?: readonly KeyValueRestrictionModel[];
  formattings?: readonly KeyValueFormattingModel[];
  expression?: string;
}

export interface FieldDisplayPresentation {
  displayTypeId: DisplayType;
  label: string;
  labelMultiple: string;
  icon: string;
}

const display = (displayTypeId: DisplayType, label: string, icon: string): FieldDisplayPresentation => ({
  displayTypeId,
  label: `modules.fields.display.${label}`,
  labelMultiple: `modules.fields.display.${label}Multiple`,
  icon,
});

export const FIELD_TYPE_LABELS: Partial<Record<FieldTypeEnum, string>> = {
  [FieldTypeEnum.Text]: 'modules.fields.types.text',
  [FieldTypeEnum.Number]: 'modules.fields.types.number',
  [FieldTypeEnum.YesNo]: 'modules.fields.types.yesNo',
  [FieldTypeEnum.Date]: 'modules.fields.types.dateTime',
  [FieldTypeEnum.Attachment]: 'modules.fields.types.attachment',
  [FieldTypeEnum.User]: 'modules.fields.types.user',
  [FieldTypeEnum.Table]: 'modules.fields.types.table',
  [FieldTypeEnum.SubModule]: 'modules.fields.types.submodule',
  [FieldTypeEnum.Formula]: 'modules.fields.types.formula',
  [FieldTypeEnum.Relation]: 'modules.fields.types.relation',
  [FieldTypeEnum.Weather]: 'modules.fields.types.weather',
  [FieldTypeEnum.Address]: 'modules.fields.types.address',
  [FieldTypeEnum.Annotation]: 'modules.fields.types.annotation',
};

interface FieldTypePresentation {
  icon: string;
  displayTypes: FieldDisplayPresentation[];
}

export const FIELD_TYPE_PRESENTATIONS: Partial<Record<FieldTypeEnum, FieldTypePresentation>> = {
  [FieldTypeEnum.Text]: {
    icon: 'rtt',
    displayTypes: [
      display(DisplayType.Text, 'text', 'rtt'),
      display(DisplayType.MultiLine, 'multiline', 'notes'),
      display(DisplayType.RichText, 'editor', 'text_fields'),
      display(DisplayType.Select, 'select', 'arrow_drop_down_circle'),
      display(DisplayType.Radio, 'radioGroup', 'radio_button_checked'),
    ],
  },
  [FieldTypeEnum.Number]: {
    icon: '123',
    displayTypes: [display(DisplayType.Text, 'number', '123')],
  },
  [FieldTypeEnum.YesNo]: {
    icon: 'check_box',
    displayTypes: [display(DisplayType.CheckBox, 'checkbox', 'check_box')],
  },
  [FieldTypeEnum.Date]: {
    icon: 'pending_actions',
    displayTypes: [display(DisplayType.Text, 'dateTime', 'pending_actions')],
  },
  [FieldTypeEnum.Attachment]: {
    icon: 'attachment',
    displayTypes: [display(DisplayType.Attachment, 'attachment', 'attachment')],
  },
  [FieldTypeEnum.User]: { icon: 'person', displayTypes: [] },
  [FieldTypeEnum.Table]: {
    icon: 'calendar_view_month',
    displayTypes: [display(DisplayType.Table, 'table', 'calendar_view_month')],
  },
  [FieldTypeEnum.SubModule]: { icon: 'account_tree', displayTypes: [] },
  [FieldTypeEnum.Formula]: { icon: 'functions', displayTypes: [] },
  [FieldTypeEnum.Relation]: {
    icon: 'link',
    displayTypes: [display(DisplayType.Select, 'select', 'arrow_drop_down_circle')],
  },
  [FieldTypeEnum.Weather]: {
    icon: 'cloud',
    displayTypes: [display(DisplayType.Weather, 'table', 'cloud')],
  },
  [FieldTypeEnum.Address]: {
    icon: 'location_on',
    displayTypes: [
      display(DisplayType.Address, 'locationNameAddress', 'location_on'),
      display(DisplayType.AddressLocationName, 'locationName', 'location_on'),
      display(DisplayType.AddressOnly, 'address', 'location_on'),
      display(DisplayType.AddressAutocomplete, 'addressAutocomplete', 'add_location_alt'),
    ],
  },
  [FieldTypeEnum.Annotation]: { icon: 'edit_note', displayTypes: [] },
};

export const fieldDisplayPresentationOf = (typeId: number, displayTypeId: number): FieldDisplayPresentation =>
  FIELD_TYPE_PRESENTATIONS[typeId as FieldTypeEnum]?.displayTypes.find(
    display => display.displayTypeId === displayTypeId,
  );

export const fieldIcon = (typeId: number, displayTypeId?: number): string =>
  fieldDisplayPresentationOf(typeId, displayTypeId)?.icon ??
  FIELD_TYPE_PRESENTATIONS[typeId as FieldTypeEnum]?.icon ??
  'rtt';

const DATE_LABELS: Partial<Record<RestrictionValueType, string>> = {
  [RestrictionValueType.Date]: 'modules.fields.restrictions.date',
  [RestrictionValueType.DateTime]: 'modules.fields.restrictions.dateAndTime',
  [RestrictionValueType.Time]: 'modules.fields.restrictions.time',
};

const NUMBER_LABELS: Partial<Record<FormattingValueType, string>> = {
  [FormattingValueType.Money]: 'modules.fields.formattings.money',
  [FormattingValueType.Percentage]: 'modules.fields.formattings.percentage',
};

export const fieldTypeIndicatorOf = (field: FieldTypeIndicatorField) => {
  const display = fieldDisplayPresentationOf(field?.typeId, field?.displayTypeId);
  let displayLabel: string;
  if (field?.typeId === FieldTypeEnum.Date) {
    const restriction = field.restrictions?.find(restriction => restriction.key === RestrictionType.DateTime);
    displayLabel = DATE_LABELS[restriction?.value];
  } else if (field?.typeId === FieldTypeEnum.Number) {
    const formatting = field.formattings?.find(formatting => formatting.key === FormattingType.Number);
    displayLabel = NUMBER_LABELS[formatting?.value];
  } else if (
    display &&
    ![DisplayType.Text, DisplayType.Attachment, DisplayType.Table, DisplayType.Weather].includes(display.displayTypeId)
  ) {
    displayLabel = field.hasMultipleValues ? display.labelMultiple : display.label;
  }
  return {
    typeLabel: FIELD_TYPE_LABELS[field?.typeId as FieldTypeEnum] ?? 'general.unknown',
    displayLabel,
    icon:
      (field?.typeId === FieldTypeEnum.Number || field?.typeId === FieldTypeEnum.Date) && field.expression?.trim()
        ? FIELD_TYPE_PRESENTATIONS[FieldTypeEnum.Formula].icon
        : fieldIcon(field?.typeId, field?.displayTypeId),
  };
};
